// ---------------------------------------------------------------------------
// selectSurfacing — shared "surface out-of-options value + remount key" logic
//
// Both SelectFieldInner (IncomeBlock) and SimpleSelectField (CustomerInformation)
// need to surface a bound value that isn't in the known option list (e.g. a
// hydrated or ZIP-autofilled value) and remount the Radix Select so that item
// is registered from its first render. This helper extracts that shared
// computation to avoid drift.
// ---------------------------------------------------------------------------

/**
 * Surfaces a bound value that isn't in the known option list and computes a
 * remount key for the Radix Select.
 * @param {string} currentValue - The currently-bound field value (canonical string).
 * @param {ReadonlyArray<{ value: string; label: string }>} options - The known option list.
 * @returns {{ resolvedOptions: Array<{ value: string; label: string }>; selectKey: string }}
 *   `resolvedOptions` is the option list, prepended with the current value as a
 *   synthetic option when it is non-empty and absent from the known list;
 *   `selectKey` changes when a value is surfaced so `<Select key={...}>` remounts
 *   and the synthetic item registers from its first render (avoids a blank trigger).
 */
export function getSurfacedSelect(
  currentValue: string,
  options: ReadonlyArray<{ value: string; label: string }>
): { resolvedOptions: Array<{ value: string; label: string }>; selectKey: string } {
  const isSurfacedValue = currentValue !== "" && !options.some((opt) => opt.value === currentValue);
  const resolvedOptions = isSurfacedValue ? [{ value: currentValue, label: currentValue }, ...options] : [...options];
  const selectKey = isSurfacedValue ? `surfaced-${currentValue}` : "options";
  return { resolvedOptions, selectKey };
}
